import { ADRESSAT_LABEL } from '../fragenkatalog';
import { ADRESSATEN, pruefeEmpfaengerEingabe, vorschlagEmpfaenger, type EmpfaengerEintrag, type EmpfaengerQuellen, type EmpfaengerVorschlag, type EmpfaengerZuordnung } from '../empfaenger';
import { MERKMAL_SCHLUESSEL } from '../grundlagen/merkmalGruppen';
import { restOffenVermerkt, ungedeckteAFragen } from '../leitfaden';
import type { StationSchluessel, StationStand } from '../leitfadenTypen';
import {
    kiBeschreibungText,
    kiErgebnisFehler,
    kiFeldLabel,
    kiMerkmalAnfragen,
    kiWertText,
    werteKiVorschlaegeAus,
    type KiAuswertung,
    type KiLeseErgebnis,
    type KiWertOption,
} from '../planungKiAnzeige';
import type { PlanungVorschlag } from '../planungVorbelegung';
import type { FrageAdressat, Planungsfrage, Planungshinweis, Planungsphase, ProjektMerkmale } from '../types';
import { DIALOG_FRAGEN, type DialogFrage } from './dialogFragen';

/**
 * Der naechste Schritt des gefuehrten Dialogs — als reine Funktion aus dem
 * Stand der Daten und dem, was der Mensch in dieser Sitzung schon geklickt
 * hat (`DialogSitzung`).
 *
 * Abschnitt „Vorhaben" (noch kein Fragenkatalog): Beschreibung → KI liest
 * sie (automatisch, Betreiber 01.11.2026) → EINE Karte „Das wissen wir
 * schon" mit allem, was Projektdaten und KI uebereinstimmend sagen → je
 * offenem Merkmal eine Frage → der Katalog entsteht von selbst.
 *
 * Danach rechnet der Dialog aus dem GESPEICHERTEN Stand (`leitfadenStand`,
 * dieselben Regeln wie Ampel und Leitfaden) und zeigt genau eine Aufgabe:
 * Empfaenger, Versand, Fristen, Bedenkenanzeige, Unterlagen, Abschluss —
 * oder dass gerade nichts zu tun ist.
 *
 * Was bleibt wie bisher: versendet wird nur per Klick, die KI entscheidet
 * nichts ohne Bestaetigung, nur ein ausdrueckliches Nein blendet Fragen
 * aus, den Fristablauf stellt der Mensch fest.
 */

// ═══════════════════════════════════════════════════════════════════════════
// EINGANG
// ═══════════════════════════════════════════════════════════════════════════

/** Was der Mensch in dieser Sitzung entschieden hat — nur im Speicher des
 *  Dialogs; beim naechsten Oeffnen rechnet der Dialog wieder aus den Daten. */
export interface DialogSitzung {
    /** „Weiter" auf der Beschreibungskarte (auch ohne Text). */
    beschreibungFertig: boolean;
    /** Die KI soll in dieser Sitzung nicht (mehr) lesen — abgebrochen oder
     *  ohne Beschreibung weiter. */
    kiAus: boolean;
    /** Zeilen der Karte „Das wissen wir schon", die nicht stimmen. Ihre
     *  Merkmale werden danach einzeln gefragt. */
    verworfen: ReadonlySet<string>;
    /** Fragen, auf die „Weiß nicht" kam (`DialogFrage.id`). */
    uebersprungen: ReadonlySet<string>;
    /** Adressaten, deren Versand „Später" bekam. */
    versandSpaeter: ReadonlySet<FrageAdressat>;
    /** „Anderer Empfänger" — die Auswahl zeigen, ohne den bisherigen Eintrag
     *  zu loeschen; geschrieben wird erst mit der neuen Wahl. */
    empfaengerWechsel: ReadonlySet<FrageAdressat>;
    /** „Später" auf den Karten Gegenstandslos und Unterlagen. */
    gegenstandslosSpaeter: boolean;
    unterlagenSpaeter: boolean;
}

export const LEERE_SITZUNG: DialogSitzung = {
    beschreibungFertig: false,
    kiAus: false,
    verworfen: new Set(),
    uebersprungen: new Set(),
    versandSpaeter: new Set(),
    empfaengerWechsel: new Set(),
    gegenstandslosSpaeter: false,
    unterlagenSpaeter: false,
};

export interface DialogLage {
    projectId: string;
    laden: boolean;
    fehler: string | null;
    isReadOnly: boolean;
    abgeschlossen: boolean;
    phase: Planungsphase | null;
    fragen: readonly Planungsfrage[];
    hinweise: readonly Planungshinweis[];
    heuteIso: string;
    /** Der ENTWURF der Grundlagen — das, was der Dialog gerade setzt. */
    entwurf: { baubeschreibung: string; bauaufgabe: string; merkmale: ProjektMerkmale; lph: number | null };
    /** Vorschlaege aus den Projektdaten (rechnen schon gegen den Entwurf). */
    vorschlaege: readonly PlanungVorschlag[];
    /** In der Vorschlagsleiste ausgeblendet — auch hier nicht anbieten. */
    vorschlaegeAusgeblendet: ReadonlySet<string>;
    ki: { ergebnis: KiLeseErgebnis | null; laeuft: boolean; ausgeblendet: ReadonlySet<string> };
    /** Stand der Stationen aus dem gespeicherten Satz (`nav.staende`). */
    staende: readonly StationStand[];
    /** Fragen, die zu geaenderten Angaben oder einer spaeteren Leistungsphase
     *  dazukommen (`nachziehbar`) — der Dialog ergaenzt sie vor dem Versand. */
    nachziehbar: number;
    /** Fragen, die nicht mehr zu den Merkmalen passen und noch nie gestellt
     *  wurden — der Sammelknopf „gegenstandslos" (`gegenstandslosBanner`). */
    nichtMehrZutreffend: number;
    /** Abgelaufene Fristen, die „feststellen" umstellen wuerde. */
    fristenAnzahl: number;
    empfaenger: {
        zuordnung: EmpfaengerZuordnung;
        laden: boolean;
        /** Ein Eintrag ist unterwegs in die Datenbank — der Versand liest
         *  die Zuordnung frisch und faende ihn sonst noch nicht. */
        speichert: boolean;
        /** Das letzte Speichern scheiterte — die Anzeige zeigt dann einen
         *  Eintrag, den der Versand nicht faende. */
        fehler: string | null;
        /** Lesen gescheitert oder Spalte fehlt — dann nur anzeigen. */
        hinweis: string | null;
    };
    empfaengerQuellen: EmpfaengerQuellen | undefined;
    /** Versand gesperrt (Uebungsprojekt usw.) — als Satz. */
    versandSperrgrund: string | null;
    sitzung: DialogSitzung;
}

// ═══════════════════════════════════════════════════════════════════════════
// AUSGANG
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Zeile der Karte „Das wissen wir schon". */
export interface BestaetigungsZeile {
    /** Stabil je Feld und Wert (`kran:true`). */
    id: string;
    feld: keyof ProjektMerkmale;
    /** „Turmdreh- oder Autokran im Einsatz". */
    label: string;
    /** „Ja", „Nein", „82 Arbeitstage". */
    wertText: string;
    nein: boolean;
    /** „Beschreibung", „Verteiler: Gerüstbau (Schulz GmbH)". */
    quelle: string;
    /** Wortlaut aus der Beschreibung, Rechenweg oder Begruendung der KI. */
    beleg: string | null;
    /** Kein Zitat — die KI hat das Nein nur eingeschaetzt. */
    geschaetzt: boolean;
    /** Was „Stimmt so" uebernimmt — genau eines von beiden. */
    ki: KiWertOption | null;
    vorschlag: PlanungVorschlag | null;
}

/** Was Projektdaten oder KI zu einer Frage sagen, ohne dass es eindeutig war. */
export interface FeldHinweis {
    wertText: string;
    quelle: string;
    beleg: string | null;
}

export type DialogSchritt =
    | { art: 'laden' }
    | { art: 'fehler'; text: string }
    /** Nur-Ansicht und noch kein Katalog: hier ist nichts zu tun. */
    | { art: 'nur_ansicht' }
    | { art: 'beschreibung'; vorlage: string; ausProjekt: boolean }
    | { art: 'ki_liest' }
    | { art: 'bestaetigen'; zeilen: BestaetigungsZeile[]; kiFehler: string | null }
    | { art: 'frage'; frage: DialogFrage; hinweise: FeldHinweis[]; nummer: number; gesamt: number; kiFehler: string | null }
    /** Alle Fragen sind beantwortet — der Katalog entsteht (automatisch). */
    | { art: 'katalog' }
    /** Neue Fragen passen dazu — sie werden ergaenzt (automatisch), bevor
     *  verschickt wird. */
    | { art: 'nachziehen'; anzahl: number }
    | { art: 'gegenstandslos'; anzahl: number }
    | {
        art: 'empfaenger';
        adressat: FrageAdressat;
        adressatLabel: string;
        anzahl: number;
        vorschlaege: EmpfaengerVorschlag[];
        /** Schon eingetragen, aber ohne brauchbare E-Mail. */
        bisher: EmpfaengerEintrag | null;
        /** Die Zuordnung liess sich nicht lesen — dann nicht schreiben. */
        hinweis: string | null;
    }
    | {
        art: 'versenden';
        adressat: FrageAdressat;
        adressatLabel: string;
        anzahl: number;
        empfaenger: EmpfaengerEintrag;
        /** Warum gerade nicht versendet werden kann — oder `null`. */
        sperrgrund: string | null;
        /** Weitere Adressaten, die danach noch dran sind. */
        danach: number;
    }
    | { art: 'fristen'; anzahl: number }
    | { art: 'bedenken'; ids: string[]; entwuerfe: number }
    | { art: 'unterlagen'; kurz: string }
    | { art: 'abschliessen' }
    | {
        art: 'warten';
        /** Fragen, die auf Antwort warten. */
        wartend: number;
        naechsteFrist: string | null;
        /** Die Saetze, die neben dem Warten stehen (Teilantworten ueber der
         *  Frist, angeforderte Unterlagen, Abschluss wartet). */
        nebenbei: string[];
        /** Adressaten mit „Später". */
        spaeter: FrageAdressat[];
        nurAnsicht: boolean;
    }
    | { art: 'abgeschlossen'; am: string | null };

export type DialogAbschnitt = 'vorhaben' | 'versand' | 'antworten' | 'abschluss';

export const DIALOG_ABSCHNITTE: readonly DialogAbschnitt[] = ['vorhaben', 'versand', 'antworten', 'abschluss'];

export const DIALOG_ABSCHNITT_LABEL: Record<DialogAbschnitt, string> = {
    vorhaben: 'Vorhaben',
    versand: 'Fragen verschicken',
    antworten: 'Antworten',
    abschluss: 'Abschluss',
};

// ═══════════════════════════════════════════════════════════════════════════
// FRAGEN
// ═══════════════════════════════════════════════════════════════════════════

const istGesetzt = (v: unknown): boolean => v !== undefined && v !== null;

const ALLE_SCHALTER = new Set<string>(MERKMAL_SCHLUESSEL as readonly string[]);

/** Ist die Frage im Entwurf schon beantwortet? Die Gruppe erst, wenn jedes
 *  ihrer Merkmale gesetzt ist. */
export const frageBeantwortet = (f: DialogFrage, e: Pick<DialogLage['entwurf'], 'merkmale' | 'lph'>): boolean => {
    const m = e.merkmale || {};
    switch (f.feld.art) {
        case 'ja_nein':
        case 'zahl':
            return istGesetzt(m[f.feld.feld]);
        case 'lph':
            return istGesetzt(e.lph);
        case 'gruppe':
            return f.feld.felder.every(k => istGesetzt(m[k]));
    }
};

/** Gilt die Frage fuer diesen Entwurf (Bedingung wie „Baujahr nur bei Bestand")? */
const frageGilt = (f: DialogFrage, m: ProjektMerkmale): boolean => !f.wenn || f.wenn(m);

/**
 * Bestaetigtes „Rest bleibt offen" (Station 3 des alten Leitfadens): die
 * offenen Ja/Nein-Merkmale gelten als bewusst ungeklaert — nicht erneut
 * fragen. Zahlen und Leistungsphase betrifft der Vermerk nicht.
 */
const vomVermerkGedeckt = (f: DialogFrage, m: ProjektMerkmale): boolean => {
    if (!restOffenVermerkt(m)) return false;
    if (f.feld.art === 'ja_nein') return ALLE_SCHALTER.has(f.feld.feld);
    if (f.feld.art === 'gruppe') return f.feld.felder.every(k => ALLE_SCHALTER.has(k));
    return false;
};

/** Die Gruppe fragt nur nach den Merkmalen, die noch offen sind. */
const mitOffenenFeldern = (f: DialogFrage, m: ProjektMerkmale): DialogFrage =>
    (f.feld.art === 'gruppe'
        ? { ...f, feld: { ...f.feld, felder: f.feld.felder.filter(k => !istGesetzt(m[k])) } }
        : f);

/** Alle Fragen, die fuer diesen Entwurf gelten, in Dialogreihenfolge. */
export const geltendeDialogFragen = (e: Pick<DialogLage['entwurf'], 'merkmale' | 'lph'>): DialogFrage[] => {
    const m = e.merkmale || {};
    return DIALOG_FRAGEN.filter(f => frageGilt(f, m) && !vomVermerkGedeckt(f, m));
};

/** Die noch offenen Fragen — ohne die mit „Weiß nicht". */
export const offeneDialogFragen = (
    e: Pick<DialogLage['entwurf'], 'merkmale' | 'lph'>,
    uebersprungen: ReadonlySet<string>,
): DialogFrage[] => {
    const m = e.merkmale || {};
    return geltendeDialogFragen(e)
        .filter(f => !frageBeantwortet(f, e) && !uebersprungen.has(f.id))
        .map(f => mitOffenenFeldern(f, m));
};

// ═══════════════════════════════════════════════════════════════════════════
// „DAS WISSEN WIR SCHON"
// ═══════════════════════════════════════════════════════════════════════════

/** Die Auswertung des letzten KI-Laufs fuer DIESES Projekt — gegen den
 *  Entwurf gerechnet (gesetzte Felder fallen heraus). */
export const kiAuswertungFuer = (l: Pick<DialogLage, 'projectId' | 'entwurf' | 'ki'>): KiAuswertung | null => {
    const e = l.ki.ergebnis;
    if (!e || e.projectId !== l.projectId || !e.lauf) return null;
    return werteKiVorschlaegeAus(e.lauf.vorschlaege, e.anfragen, l.entwurf.merkmale, l.ki.ausgeblendet);
};

interface Kandidat {
    wert: boolean | number;
    wertText: string;
    quelle: string;
    beleg: string | null;
    geschaetzt: boolean;
    ki: KiWertOption | null;
    vorschlag: PlanungVorschlag | null;
}

const KI_QUELLE = 'Beschreibung';

const kiKandidat = (o: KiWertOption): Kandidat => {
    const b = o.belege[0];
    const beleg = b ? (b.zitat ? `„${b.zitat}“` : b.einschaetzung) : null;
    return { wert: o.wert, wertText: kiWertText(o), quelle: KI_QUELLE, beleg: beleg || null, geschaetzt: o.geschaetzt, ki: o, vorschlag: null };
};

const vorschlagKandidat = (v: PlanungVorschlag): Kandidat | null => {
    if (v.art === 'merkmal') {
        return { wert: true, wertText: 'Ja', quelle: v.quelle, beleg: v.rechenweg || null, geschaetzt: false, ki: null, vorschlag: v };
    }
    if (v.art === 'zahl') {
        return {
            wert: v.wert,
            wertText: kiWertText({ typ: 'zahl', feld: v.feld, wert: v.wert }),
            quelle: v.quelle, beleg: v.rechenweg || null, geschaetzt: false, ki: null, vorschlag: v,
        };
    }
    return null;
};

/** Je Feld alle Kandidaten aus KI und Projektdaten, in Maskenreihenfolge der KI. */
const kandidatenJeFeld = (l: Pick<DialogLage, 'projectId' | 'entwurf' | 'ki' | 'vorschlaege' | 'vorschlaegeAusgeblendet'>): Map<string, Kandidat[]> => {
    const jeFeld = new Map<string, Kandidat[]>();
    const nimm = (feld: string, k: Kandidat) => {
        const liste = jeFeld.get(feld) || [];
        liste.push(k);
        jeFeld.set(feld, liste);
    };
    const ki = kiAuswertungFuer(l);
    (ki ? ki.gruppen : []).forEach(g => g.optionen.forEach(o => nimm(g.feld, kiKandidat(o))));
    const m = l.entwurf.merkmale || {};
    l.vorschlaege.forEach(v => {
        if (l.vorschlaegeAusgeblendet.has(v.id) || !v.feld) return;
        if (istGesetzt(m[v.feld as keyof ProjektMerkmale])) return;
        const k = vorschlagKandidat(v);
        if (k) nimm(v.feld, k);
    });
    return jeFeld;
};

/** Reihenfolge der Zeilen: wie die Fragen des Dialogs. */
const RANG = new Map<string, number>();
DIALOG_FRAGEN.forEach((f, i) => {
    if (f.feld.art === 'gruppe') f.feld.felder.forEach((k, j) => RANG.set(k, i + j / 100));
    else if (f.feld.art !== 'lph') RANG.set(f.feld.feld, i);
});
const rang = (feld: string): number => RANG.get(feld) ?? 999;

/**
 * Die Zeilen der Karte „Das wissen wir schon": je Feld, zu dem Projektdaten
 * und KI dasselbe sagen, eine Zeile. Sagen sie Verschiedenes (oder die KI
 * widerspricht sich selbst), gibt es keine Zeile — das Feld wird gefragt und
 * zeigt die Angaben als Hinweis (`feldHinweise`). Verworfene Zeilen fehlen.
 *
 * Stimmen beide Quellen ueberein, uebernimmt die Zeile den Vorschlag aus den
 * Projektdaten und nennt beide Quellen.
 */
export const bestaetigungsZeilen = (l: Pick<DialogLage, 'projectId' | 'entwurf' | 'ki' | 'vorschlaege' | 'vorschlaegeAusgeblendet' | 'sitzung'>): BestaetigungsZeile[] => {
    const zeilen: BestaetigungsZeile[] = [];
    kandidatenJeFeld(l).forEach((kandidaten, feld) => {
        const werte = new Set(kandidaten.map(k => String(k.wert)));
        if (werte.size !== 1) return;
        const id = `${feld}:${String(kandidaten[0].wert)}`;
        if (l.sitzung.verworfen.has(id)) return;
        const ausProjekt = kandidaten.find(k => k.vorschlag);
        const ausKi = kandidaten.find(k => k.ki);
        const fuehrend = ausProjekt || ausKi || kandidaten[0];
        const quellen = [...new Set(kandidaten.map(k => k.quelle))];
        zeilen.push({
            id,
            feld: feld as keyof ProjektMerkmale,
            label: kiFeldLabel(feld),
            wertText: fuehrend.wertText,
            nein: fuehrend.wert === false,
            quelle: quellen.join(' · '),
            beleg: (ausKi && ausKi.beleg) || fuehrend.beleg,
            geschaetzt: !ausProjekt && kandidaten.every(k => k.geschaetzt),
            ki: ausProjekt ? null : (ausKi ? ausKi.ki : null),
            vorschlag: ausProjekt ? ausProjekt.vorschlag : null,
        });
    });
    return zeilen.sort((a, b) => rang(a.feld) - rang(b.feld));
};

/** Was zu einem gefragten Feld vorlag, aber nicht eindeutig war oder
 *  verworfen wurde — als Hinweis unter der Frage. */
export const feldHinweise = (
    l: Pick<DialogLage, 'projectId' | 'entwurf' | 'ki' | 'vorschlaege' | 'vorschlaegeAusgeblendet'>,
    frage: DialogFrage,
): FeldHinweis[] => {
    if (frage.feld.art !== 'ja_nein' && frage.feld.art !== 'zahl') return [];
    const kandidaten = kandidatenJeFeld(l).get(frage.feld.feld) || [];
    return kandidaten.map(k => ({ wertText: k.wertText, quelle: k.quelle, beleg: k.beleg }));
};

// ═══════════════════════════════════════════════════════════════════════════
// VERSAND
// ═══════════════════════════════════════════════════════════════════════════

/** Ein Eintrag, an den versendet werden kann: Name und gueltige E-Mail. */
export const versendbar = (e: EmpfaengerEintrag | null | undefined): e is EmpfaengerEintrag =>
    !!e && !!String(e.email || '').trim() && pruefeEmpfaengerEingabe(e.name, e.email || '') === null;

/** Adressaten mit noch nie gestellten Fragen, in Anzeigereihenfolge, mit Anzahl. */
export const versandFolge = (fragen: readonly Pick<Planungsfrage, 'adressat' | 'status'>[]): { adressat: FrageAdressat; anzahl: number }[] => {
    const zahl = new Map<FrageAdressat, number>();
    fragen.forEach(f => {
        if (f.status !== 'offen') return;
        new Set(f.adressat || []).forEach(a => zahl.set(a, (zahl.get(a) || 0) + 1));
    });
    return ADRESSATEN.filter(a => zahl.has(a)).map(a => ({ adressat: a, anzahl: zahl.get(a) || 0 }));
};

// ═══════════════════════════════════════════════════════════════════════════
// DER NAECHSTE SCHRITT
// ═══════════════════════════════════════════════════════════════════════════

/** Soll die KI jetzt (automatisch) lesen bzw. liest sie gerade? */
export const kiSollLesen = (l: Pick<DialogLage, 'projectId' | 'isReadOnly' | 'abgeschlossen' | 'entwurf' | 'ki' | 'sitzung'>): boolean => {
    if (l.ki.laeuft) return true;
    if (l.isReadOnly || l.abgeschlossen || l.sitzung.kiAus) return false;
    const e = l.ki.ergebnis;
    if (e && e.projectId === l.projectId) return false;
    if (!kiBeschreibungText(l.entwurf.baubeschreibung, l.entwurf.bauaufgabe)) return false;
    // Nur, wenn der Dialog noch etwas fragen wuerde — die KI fragt auch nach
    // Feldern, die hier nicht gelten (Baujahr ohne Bestand).
    if (!offeneDialogFragen(l.entwurf, l.sitzung.uebersprungen).length) return false;
    return kiMerkmalAnfragen(l.entwurf.merkmale).length > 0;
};

const kiFehlerFuer = (l: Pick<DialogLage, 'projectId' | 'ki'>): string | null => {
    const e = l.ki.ergebnis;
    return e && e.projectId === l.projectId ? kiErgebnisFehler(e) : null;
};

const stand = (staende: readonly StationStand[], s: StationSchluessel): StationStand | undefined =>
    staende.find(x => x.schluessel === s);

/** `aktuell` ist im Leitfaden nur das hervorgehobene `offen`. */
const istOffen = (s: StationStand | undefined): boolean =>
    !!s && (s.zustand === 'offen' || s.zustand === 'aktuell');

const vorhaben = (l: DialogLage): DialogSchritt => {
    if (l.isReadOnly) return { art: 'nur_ansicht' };
    // Ein laufender Lauf zuerst — sonst haengte der Wechsel der Karte ihn nicht
    // aus, aber man saehe ihn nicht und koennte ihn nicht abbrechen.
    if (l.ki.laeuft) return { art: 'ki_liest' };
    if (!String(l.entwurf.baubeschreibung || '').trim() && !l.sitzung.beschreibungFertig) {
        const v = l.vorschlaege.find(x => x.art === 'baubeschreibung' && !l.vorschlaegeAusgeblendet.has(x.id));
        const vorlage = v && v.art === 'baubeschreibung' ? v.wert : '';
        return { art: 'beschreibung', vorlage, ausProjekt: !!vorlage };
    }
    if (kiSollLesen(l)) return { art: 'ki_liest' };
    const kiFehler = kiFehlerFuer(l);
    const zeilen = bestaetigungsZeilen(l);
    if (zeilen.length) return { art: 'bestaetigen', zeilen, kiFehler };
    const offen = offeneDialogFragen(l.entwurf, l.sitzung.uebersprungen);
    if (offen.length) {
        const gesamt = geltendeDialogFragen(l.entwurf).length;
        return {
            art: 'frage',
            frage: offen[0],
            hinweise: feldHinweise(l, offen[0]),
            nummer: Math.max(1, gesamt - offen.length + 1),
            gesamt,
            kiFehler,
        };
    }
    return { art: 'katalog' };
};

/**
 * Genau ein Schritt. Reihenfolge nach dem Katalog: erst was den Versand
 * betrifft (unpassende Fragen nicht mitschicken, dann je Adressat Empfaenger
 * und Versand), dann was eine Handlung verlangt (Fristen, Bedenkenanzeige),
 * dann Unterlagen und Abschluss — und wenn nichts davon dran ist: warten.
 */
export const naechsterSchritt = (l: DialogLage): DialogSchritt => {
    if (l.laden) return { art: 'laden' };
    if (l.fehler) return { art: 'fehler', text: l.fehler };
    if (l.abgeschlossen) return { art: 'abgeschlossen', am: l.phase ? l.phase.abgeschlossen_am || null : null };
    if (!l.fragen.length) return vorhaben(l);

    const spaeter = ADRESSATEN.filter(a => l.sitzung.versandSpaeter.has(a));
    const nebenbei: string[] = [];
    const warten = (): DialogSchritt => {
        const antworten = stand(l.staende, 'antworten');
        const wartend = l.fragen.filter(f => f.status === 'gestellt' || f.status === 'teilbeantwortet').length;
        return {
            art: 'warten',
            wartend,
            naechsteFrist: antworten && antworten.zustand === 'wartet' ? antworten.naechsteFrist : null,
            nebenbei,
            spaeter,
            nurAnsicht: l.isReadOnly,
        };
    };
    if (l.isReadOnly) return warten();

    // Erst vervollstaendigen, dann verschicken — sonst ginge eine
    // Klaerungsliste ohne die neuen Fragen hinaus.
    if (l.nachziehbar > 0) return { art: 'nachziehen', anzahl: l.nachziehbar };

    if (l.nichtMehrZutreffend > 0 && !l.sitzung.gegenstandslosSpaeter) {
        return { art: 'gegenstandslos', anzahl: l.nichtMehrZutreffend };
    }

    const folge = versandFolge(l.fragen).filter(z => !l.sitzung.versandSpaeter.has(z.adressat));
    if (folge.length) {
        // Erst die Zuordnung lesen — sonst blitzte kurz ein spaeterer Schritt auf.
        if (l.empfaenger.laden) return { art: 'laden' };
        const { adressat, anzahl } = folge[0];
        const eintrag = l.empfaenger.zuordnung[adressat] || null;
        const adressatLabel = ADRESSAT_LABEL[adressat];
        if (!versendbar(eintrag) || l.sitzung.empfaengerWechsel.has(adressat)) {
            return {
                art: 'empfaenger', adressat, adressatLabel, anzahl,
                vorschlaege: vorschlagEmpfaenger(adressat, l.empfaengerQuellen),
                bisher: eintrag,
                hinweis: l.empfaenger.hinweis,
            };
        }
        return {
            art: 'versenden', adressat, adressatLabel, anzahl, empfaenger: eintrag,
            sperrgrund: l.versandSperrgrund
                || l.empfaenger.hinweis
                || (l.empfaenger.speichert ? 'Der Empfänger wird gerade gespeichert …' : null)
                || (l.empfaenger.fehler
                    ? `Der Empfänger ist nicht gespeichert (${l.empfaenger.fehler}). Wählen Sie ihn über „Anderer Empfänger“ erneut.`
                    : null),
            danach: folge.length - 1,
        };
    }

    const antworten = stand(l.staende, 'antworten');
    if (l.fristenAnzahl > 0) return { art: 'fristen', anzahl: l.fristenAnzahl };

    const bedenken = stand(l.staende, 'bedenken');
    if (bedenken && (bedenken.zustand === 'handlungsbedarf' || istOffen(bedenken))) {
        const ids = ungedeckteAFragen([...l.fragen], [...l.hinweise], l.heuteIso).map(f => f.id);
        const entwuerfe = l.hinweise.filter(h => !h.versendet_am && (h.art === 'bedenken' || h.art === 'gefahr')).length;
        if (ids.length || entwuerfe) return { art: 'bedenken', ids, entwuerfe };
    }

    if (antworten && istOffen(antworten) && antworten.kurz) nebenbei.push(antworten.kurz);

    const unterlagen = stand(l.staende, 'unterlagen');
    if (unterlagen && istOffen(unterlagen) && !l.sitzung.unterlagenSpaeter) {
        return { art: 'unterlagen', kurz: unterlagen.kurz };
    }
    if (unterlagen && (unterlagen.zustand === 'wartet' || istOffen(unterlagen)) && unterlagen.kurz) {
        nebenbei.push(`Unterlagen: ${unterlagen.kurz}`);
    }

    const abschluss = stand(l.staende, 'abschluss');
    if (istOffen(abschluss)) return { art: 'abschliessen' };
    if (abschluss && abschluss.zustand === 'wartet' && abschluss.kurz) nebenbei.push(`Abschluss: ${abschluss.kurz}`);

    return warten();
};

/** Zu welchem der vier Abschnitte ein Schritt gehoert. */
export const abschnittVon = (s: DialogSchritt): DialogAbschnitt => {
    switch (s.art) {
        case 'nachziehen':
        case 'gegenstandslos':
        case 'empfaenger':
        case 'versenden':
            return 'versand';
        case 'fristen':
        case 'bedenken':
        case 'unterlagen':
        case 'warten':
            return 'antworten';
        case 'abschliessen':
        case 'abgeschlossen':
            return 'abschluss';
        default:
            return 'vorhaben';
    }
};

export type AbschnittStand = 'erledigt' | 'aktuell' | 'offen';

/** Die Fortschrittsleiste: Abschnitte vor dem aktuellen sind erledigt. */
export const fortschritt = (s: DialogSchritt): { abschnitt: DialogAbschnitt; stand: AbschnittStand }[] => {
    const jetzt = DIALOG_ABSCHNITTE.indexOf(abschnittVon(s));
    const fertig = s.art === 'abgeschlossen';
    return DIALOG_ABSCHNITTE.map((abschnitt, i) => ({
        abschnitt,
        stand: fertig || i < jetzt ? 'erledigt' : i === jetzt ? 'aktuell' : 'offen',
    }));
};
